import { ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Chip, Separator, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";

import { UserEntity } from "@/features/users/type.ts";
import { useUser } from "@/features/users/use-users.ts";
import {
  UserCityEdit,
  UserNameEdit,
  UserRoleEdit,
} from "@/features/users/ui/user-inline-edits.tsx";
import { UserOrders } from "@/features/users/ui/user-orders.tsx";
import { useCities } from "@/features/city/use-cities.ts";
import { formatDateTime } from "@/shared/helper/datetime.ts";
import { userTools } from "@/shared/helper/user.ts";

export const Route = createFileRoute("/dashboard/users/$userId")({
  component: RouteComponent,
});

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-row gap-4 items-center min-h-10">
    <span className="w-44 shrink-0 text-sm text-default-500">{label}</span>
    <div className="text-sm">{children}</div>
  </div>
);

const UserHeader = ({ user }: { user: UserEntity }) => {
  const cities = useCities();

  const city = cities.data.data.find((item) => item.id === user.city);

  return (
    <div className="flex flex-col gap-2">
      <Link
        className="flex flex-row items-center gap-1 text-sm text-default-500 hover:text-default-700 w-fit"
        to="/dashboard/users"
      >
        <Icon className="size-4" icon="solar:arrow-left-outline" />
        Пользователи
      </Link>

      <div className="flex flex-row flex-wrap items-center gap-3">
        <p className="font-bold text-xl">{userTools.fullName(user)}</p>

        {user.banned && (
          <Chip color="danger" size="sm" variant="soft">
            Заблокирован
          </Chip>
        )}
      </div>

      <div className="flex flex-row flex-wrap items-center gap-2 text-sm text-default-500">
        <span className="flex flex-row items-center gap-1">
          {user.phoneNumber ?? "Без номера"}
          {user.phoneNumberVerified ? (
            <Icon
              className="size-4 text-success-400"
              icon="ion:checkmark-circle"
            />
          ) : (
            <Icon className="size-4 text-neutral-400" icon="ion:close-circle" />
          )}
        </span>
        <span>·</span>
        <span>{userTools.role.toString(user.role)}</span>
        <span>·</span>
        <span>{city?.name ?? "Город не указан"}</span>
      </div>
    </div>
  );
};

const UserProfile = ({ user }: { user: UserEntity }) => (
  <div className="flex flex-col gap-1">
    <p className="font-bold text-lg mb-2">Профиль</p>

    <Field label="Имя">
      <UserNameEdit name={user.name} userId={user.id} />
    </Field>

    <Field label="Роль">
      <UserRoleEdit role={user.role} userId={user.id} />
    </Field>

    <Field label="Город">
      <UserCityEdit cityId={user.city} userId={user.id} />
    </Field>

    <Field label="Телефон">{user.phoneNumber ?? "—"}</Field>

    <Field label="Email">{user.email || "—"}</Field>

    <Field label="Тип">{userTools.type.toString(user.type)}</Field>

    <Field label={user.type === "company" ? "БИН" : "ИИН"}>
      {(user.type === "company" ? user.bin : user.iin) || "—"}
    </Field>

    <Field label="Дата рождения">{user.birthDay || "—"}</Field>

    <Field label="Язык">{user.lang ?? "—"}</Field>

    <Field label="Регистрация">{formatDateTime(user.createdAt)}</Field>

    <Field label="Обновлён">{formatDateTime(user.updatedAt)}</Field>

    {user.banned && (
      <Field label="Причина блокировки">{user.banReason || "—"}</Field>
    )}

    <Field label="ID">
      <span className="font-mono text-xs text-default-500">{user.id}</span>
    </Field>
  </div>
);

function RouteComponent() {
  const { userId } = Route.useParams();

  const { data, isLoading, isError } = useUser(userId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const user = data?.data;

  if (isError || !user) {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <p className="text-default-500">Пользователь не найден</p>
        <Link className="text-sm underline" to="/dashboard/users">
          К списку пользователей
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-4">
      <UserHeader user={user} />

      <Separator />

      <UserProfile user={user} />

      <Separator />

      <UserOrders userId={user.id} />
    </div>
  );
}
